import { z } from 'zod';
import zweckeData from '../data/medienzwecke.json' with { type: 'json' };
import { getPruefzyklen } from './pruefzyklen.js';
import { Aufbewahrungsklasse } from './schemas/medium.js';
import { katalogFreigabe } from './dokumentkatalog.js';
/**
 * Zweck-Katalog für Fotos und Videos.
 *
 * Dieselbe Linie wie `pruefzyklen.json` und `dokumentkatalog.json`: Der
 * Anwender soll an keiner Stelle raten müssen, welche Aufnahme jetzt sinnvoll
 * ist. Jeder Eintrag sagt, in welcher Situation er gilt (`kontext`), was
 * aufgenommen wird (`art`), wie wichtig das ist (`gewicht`) und wie das Bild
 * aussehen soll (`anleitung`, `beispiel`). Das `kuerzel` wandert in den
 * Anzeigenamen der Datei (`Mangel-0042-Detail`), die `aufbewahrungsklasse`
 * ist die Voreinstellung für das Medium.
 *
 * Solange `freigabe.status === 'entwurf'` ist, sind die Gewichte
 * Voreinstellungen, keine fachliche Vorgabe.
 */
export const Medienkontext = z.enum([
    'mangel',
    'anlage',
    'begehung',
    'pruefung',
    'uebernahme',
    'aufgabe',
    'grundriss',
    'freimeldung',
    'uebung',
    'unterweisung',
]);
export const MEDIENKONTEXT_LABEL = {
    mangel: 'Mangel',
    anlage: 'Anlage',
    begehung: 'Begehung',
    pruefung: 'Prüfung',
    uebernahme: 'Übernahme',
    aufgabe: 'Aufgabe',
    grundriss: 'Grundriss',
    freimeldung: 'Freimeldung',
    uebung: 'Räumungsübung',
    unterweisung: 'Unterweisung',
};
/**
 * Was aufgenommen wird. `foto_oder_dokument` ist der Fall „Protokoll,
 * Unterlage, Teilnehmerliste": ein Foto vom Papier ist erlaubt, ein PDF ist
 * besser.
 */
export const MedienzweckArt = z.enum(['foto', 'video', 'foto_oder_dokument']);
export const MEDIENZWECK_ART_LABEL = {
    foto: 'Foto',
    video: 'Video',
    foto_oder_dokument: 'Foto oder PDF',
};
/**
 * Wie wichtig eine Aufnahme ist. Steuert später, ob ihr Fehlen ein offener
 * Punkt wird: `pflicht` immer, die drei bedingten nur, wenn die Bedingung
 * zutrifft (siehe `istPflicht`), `empfohlen` nie — nur ein Hinweis.
 */
export const MedienzweckGewicht = z.enum([
    'pflicht',
    'pflicht_bei_schwer',
    'pflicht_bei_pruefpflichtig',
    'pflicht_bei_fremdpruefung',
    'pflicht_je_eintrag',
    'pflicht_laut_katalog',
    'empfohlen',
]);
export const MEDIENZWECK_GEWICHT_LABEL = {
    pflicht: 'Pflicht',
    pflicht_bei_schwer: 'Pflicht bei schwerem Mangel',
    pflicht_bei_pruefpflichtig: 'Pflicht bei prüfpflichtiger Anlage',
    pflicht_bei_fremdpruefung: 'Pflicht bei Fremdprüfung',
    pflicht_je_eintrag: 'Pflicht je erwartetem Eintrag',
    pflicht_laut_katalog: 'Pflicht, wenn der Katalog einen Nachweis verlangt',
    empfohlen: 'Empfohlen',
};
export const medienzweck = z.object({
    /** Stabiler Schlüssel, z. B. `mangel_detail`. Wird in `Medium.zweck` gespeichert. */
    key: z.string().min(1).max(60),
    titel: z.string().min(1),
    kontext: Medienkontext,
    art: MedienzweckArt,
    gewicht: MedienzweckGewicht,
    /** Ein Satz: Wie soll das Bild aussehen? Steht auf der Kachel. */
    anleitung: z.string().min(1),
    /** Ein konkretes Beispiel für den Tooltip. */
    beispiel: z.string().min(1),
    mindestanzahl: z.number().int().min(1),
    /** Nur bei Video: längstes zulässiges Video in Sekunden. */
    maxDauerSek: z.number().int().positive().nullable(),
    /** Kürzel im Anzeigenamen der Datei — ASCII, ohne Leerzeichen, eindeutig je Kontext. */
    kuerzel: z.string().regex(/^[A-Za-z0-9-]+$/),
    aufbewahrungsklasse: Aufbewahrungsklasse,
    /** Ob ein Medium dieses Zwecks von vornherein für den Kunden sichtbar ist. */
    fuerKundeSichtbarDefault: z.boolean(),
    /** Einordnung, wo der Eintrag eine Erklärung braucht. */
    hinweis: z.string().nullable(),
});
const medienzweckeKatalog = z.object({
    version: z.string(),
    lastVerified: z.string(),
    freigabe: katalogFreigabe,
    zwecke: z.array(medienzweck),
});
// Beim Laden geprüft: Ein Tippfehler im Katalog fällt so beim Start auf,
// nicht erst, wenn ein Anwender die betroffene Kachel sieht.
const raw = medienzweckeKatalog.parse(zweckeData);
export const medienzweckeVersion = raw.version;
export const medienzweckeLastVerified = raw.lastVerified;
export const medienzweckeFreigabe = raw.freigabe;
const ZWECK_BY_KEY = new Map(raw.zwecke.map((z) => [z.key, z]));
/** Sind die Zwecke fachlich freigegeben? Steuert den Vorbehalt in der Oberfläche. */
export function medienzweckeSindFreigegeben() {
    return raw.freigabe.status === 'freigegeben';
}
export function getMedienzwecke() {
    return raw.zwecke;
}
export function getMedienzweck(key) {
    return ZWECK_BY_KEY.get(key) ?? null;
}
/** Die Zwecke einer Situation, in Katalogreihenfolge — das ist die Reihenfolge der Kacheln. */
export function zweckeFuerKontext(kontext) {
    return raw.zwecke.filter((z) => z.kontext === kontext);
}
/**
 * Welche Schweregrade als „schwer" gelten.
 *
 * Aus der Handlungsfolge abgeleitet: „Gefahr im Verzug" und „Erheblich" haben
 * eine Frist, in der jemand etwas tun muss — dafür braucht der Bericht den
 * Beleg. Geringfügiges und Empfehlungen nicht.
 */
export const SCHWERE_MIT_FOTOPFLICHT = new Set([
    'gefahr',
    'erheblich',
]);
export function istSchwererMangel(schwere) {
    return schwere !== null && schwere !== undefined && SCHWERE_MIT_FOTOPFLICHT.has(schwere);
}
/**
 * Ist diese Aufnahme in der gegebenen Situation Pflicht?
 *
 * Bedingte Gewichte werden nur wahr, wenn die Bedingung BELEGT ist. Fehlt die
 * Angabe (`undefined`/`null`), ist die Aufnahme nicht Pflicht — ein Zwang auf
 * geratener Grundlage wäre die falsche Richtung, und der Katalog weiß nichts
 * über die konkrete Anlage.
 */
export function istPflicht(zweck, bedingungen = {}) {
    switch (zweck.gewicht) {
        case 'pflicht':
            return true;
        case 'pflicht_bei_schwer':
            return istSchwererMangel(bedingungen.schwere);
        case 'pflicht_bei_pruefpflichtig':
            return bedingungen.pruefpflichtig === true;
        case 'pflicht_bei_fremdpruefung':
            return bedingungen.fremdpruefung === true;
        case 'pflicht_je_eintrag':
            return bedingungen.eintragErwartet === true;
        case 'pflicht_laut_katalog':
            return bedingungen.nachweisLautKatalog === true;
        case 'empfohlen':
            return false;
    }
}
/**
 * Ist eine Anlage dieser Art prüfpflichtig?
 *
 * Genau eine Definition für alle Masken: Prüfpflichtig ist, wofür der
 * Prüfzyklen-Katalog mindestens einen Zyklus MIT Intervall führt. Ein Zyklus
 * ohne Intervall sagt gerade, dass sich keine allgemeine Frist belegen ließ
 * (Sprinkler, Abschottungen) — daraus eine Fotopflicht abzuleiten, hieße eine
 * Pflicht auf eine Wissenslücke zu stützen.
 *
 * Das ist die Bedingung hinter dem Gewicht `pflicht_bei_pruefpflichtig`.
 */
export function istPruefpflichtig(typ) {
    return getPruefzyklen(typ).some((z) => z.intervallMonate !== null);
}
/**
 * Größengrenzen für den Upload in Megabyte.
 *
 * Dieselben Zahlen wie die Defaults von `MEDIEN_MAX_FOTO_MB`,
 * `MEDIEN_MAX_VIDEO_MB` und `MEDIEN_MAX_DOKUMENT_MB` in `apps/api/src/env.ts`
 * (dort gelesen in `config/medien.ts`). Der Server meldet seine tatsächlichen
 * Werte heute nicht ans Web; die Oberfläche nutzt diese Zahlen deshalb nur für
 * den Hinweis VOR dem Upload. Verbindlich prüft der Server — hebt ein Betrieb
 * seine Env-Werte an, ist der Hinweis hier zu streng, nicht zu lax. Sobald es
 * eine Konfigurationsroute gibt, tritt sie an diese Stelle.
 */
export const MEDIEN_GRENZEN = {
    fotoMaxMb: 25,
    videoMaxMb: 200,
    dokumentMaxMb: 50,
};
